import { useState, useEffect } from 'react';
import { AntConst, AntResolveServiceRoutingHeaders } from 'ante-react-kit';

// Đọc session từ localStorage giống BaseAPI.upload — keys namespace theo System_Code.
function getSessionHeaders() {
    var sysCode = AntConst.System_Code;
    var token = localStorage.getItem(sysCode + '-session-token');
    var infoRaw = localStorage.getItem(sysCode + '-session-info');
    var info = {};
    try { info = infoRaw ? JSON.parse(infoRaw) : {}; } catch (e) {}
    return {
        Authorization: 'Bearer ' + (token || ''),
        'Business-Short-Code': info.business_short_code,
        'Business-Zone-Code': info.business_zone_code,
        'Business-Industry': info.business_industry,
        'Business-Sub-Industry': info.business_sub_industry,
        ...AntResolveServiceRoutingHeaders(info)
    };
}

function getBaseURL() {
    var sysCode = AntConst.System_Code;
    var BaseURL = localStorage.getItem(sysCode + '-Sys_BaseURL');
    if (!BaseURL || BaseURL === 'null' || BaseURL === 'undefined') BaseURL = AntConst.BaseURL;
    return BaseURL || '';
}

// TIER 1 — Helper rename file để tránh collision: "name.pdf" → "name (1).pdf"
export function suggestNewName(originalName, existingNames) {
    var dot = originalName.lastIndexOf('.');
    var base = dot > 0 ? originalName.substring(0, dot) : originalName;
    var ext = dot > 0 ? originalName.substring(dot) : '';
    var i = 1;
    var candidate = base + ' (' + i + ')' + ext;
    while (existingNames.indexOf(candidate) !== -1) {
        i++;
        candidate = base + ' (' + i + ')' + ext;
    }
    return candidate;
}

class UploadManagerImpl {
    constructor() {
        this.queue = [];
        this.listeners = new Set();
        this.maxConcurrent = 3;
    }
    subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
    notify() {
        var snap = this.queue.slice();
        this.listeners.forEach(function(fn) { try { fn(snap); } catch (e) {} });
    }
    enqueue(files, opts) {
        var self = this;
        opts = opts || {};
        files.forEach(function(f) {
            self.queue.push({
                id: Math.random().toString(36).substring(2, 10),
                file: f,
                // TIER 1: override file name khi user chọn "Đổi tên" trong replace dialog
                override_name: opts.override_name || null,
                folder_code: opts.folder_code || null,
                voucher: opts.voucher || null,
                // Khi true → BE sẽ overwrite file cùng tên (replace). TODO: BE cần hỗ trợ.
                replace_existing: !!opts.replace_existing,
                // Relative path từ webkitRelativePath (folder upload) — nếu set, BE/FE rebuild folder structure
                relative_path: opts.relative_path || f.webkitRelativePath || null,
                status: 'pending',
                progress: 0,
                error: null,
                meddoc_code: null
            });
        });
        this.notify();
        this.startNext();
    }
    async startNext() {
        var running = this.queue.filter(function(q) { return q.status === 'uploading'; }).length;
        if (running >= this.maxConcurrent) return;
        var next = this.queue.find(function(q) { return q.status === 'pending'; });
        if (!next) return;
        next.status = 'uploading';
        this.notify();
        try {
            var fd = new FormData();
            // Khi có override_name → tạo File mới có name mới
            if (next.override_name) {
                try {
                    var renamed = new File([next.file], next.override_name, { type: next.file.type });
                    fd.append('files', renamed);
                } catch (e) {
                    fd.append('files', next.file);
                    fd.append('override_name', next.override_name);
                }
            } else {
                fd.append('files', next.file);
            }
            if (next.folder_code) fd.append('folder_code', next.folder_code);
            if (next.relative_path) fd.append('relative_path', next.relative_path);
            if (next.replace_existing) fd.append('replace_existing', '1');
            if (next.voucher) {
                fd.append('voucher_type', next.voucher.voucher_type);
                fd.append('voucher_code', next.voucher.voucher_code);
                if (next.voucher.voucher_id) fd.append('voucher_id', next.voucher.voucher_id);
            }
            var self = this;
            var result = await new Promise(function(resolve, reject) {
                var xhr = new XMLHttpRequest();
                var url = (getBaseURL() + '/media/MEDDocument/upload').replace(/([^:])\/+/g, '$1/');
                xhr.open('POST', url, true);
                var headers = getSessionHeaders();
                Object.keys(headers).forEach(function(k) {
                    if (headers[k] !== undefined && headers[k] !== null) {
                        xhr.setRequestHeader(k, headers[k]);
                    }
                });
                xhr.upload.onprogress = function(e) {
                    if (e.lengthComputable) {
                        next.progress = Math.round(e.loaded / e.total * 100);
                        self.notify();
                    }
                };
                xhr.onload = function() {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try { resolve(JSON.parse(xhr.responseText)); }
                        catch (err) { reject(err); }
                    } else {
                        var msg = 'HTTP ' + xhr.status;
                        try {
                            var body = JSON.parse(xhr.responseText);
                            if (body && (body.message || body.error)) msg = body.message || body.error;
                        } catch (e) {}
                        reject(new Error(msg));
                    }
                };
                xhr.onerror = function() { reject(new Error('Network error')); };
                xhr.send(fd);
            });
            if (result && result.error) {
                throw new Error(result.message || result.error || 'Upload failed');
            }
            next.status = 'done';
            next.progress = 100;
            if (result && result.rows && result.rows[0]) next.meddoc_code = result.rows[0].code;
        } catch (e) {
            next.status = 'failed';
            next.error = e && e.message;
        }
        this.notify();
        this.startNext();
    }
    cancel(id) {
        var item = this.queue.find(function(q) { return q.id === id; });
        if (item && item.status === 'pending') item.status = 'cancelled';
        else if (item && item.status === 'done') this.queue = this.queue.filter(function(q) { return q.id !== id; });
        this.notify();
    }
    clear() {
        this.queue = this.queue.filter(function(q) { return q.status !== 'done'; });
        this.notify();
    }
}

export const uploadManager = new UploadManagerImpl();

export function useUploadQueue() {
    var [queue, setQueue] = useState(uploadManager.queue.slice());
    useEffect(function() {
        var unsub = uploadManager.subscribe(setQueue);
        return unsub;
    }, []);
    return queue;
}
